import {
    NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS,
    NATIVE_INVOICE_PDF_FROM_FIELD_IDS,
    NATIVE_INVOICE_PDF_HEADER_FIELD_IDS,
    NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS,
    NATIVE_INVOICE_PDF_NOTES_FIELD_IDS,
    NATIVE_INVOICE_PDF_TO_FIELD_IDS,
    NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS,
} from "@common/helpers/applyNativeInvoicePdfFieldSettings";
import type {
    NativeInvoiceFieldSetting,
    NativeInvoiceFieldSettings,
    NativeInvoicePdfFieldSettings,
} from "@common/schemas/domains/invoice/native-invoice-field-settings.schema";

export type InvoiceFieldSetting = NativeInvoiceFieldSetting;
export type InvoiceFieldSettings = NativeInvoiceFieldSettings;
export type InvoiceFieldSettingsDraft = NativeInvoicePdfFieldSettings;

export interface InvoiceFieldDefinition {
    id: string;
    name: string;
    canHide: boolean;
    isIncludedByDefault: boolean;
}

// Single row for the top-left document heading ("INVOICE" by default).
const HEADER_FIELD_NAMES = {
    invoiceHeading: "Header",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_HEADER_FIELD_IDS)[number],
    string
>;

const FROM_FIELD_NAMES = {
    from: "From",
    businessName: "Display name",
    fullName: "Full name",
    sender: "Sender",
    replyTo: "Reply to",
    street1: "Street line 1",
    street2: "Street line 2",
    city: "City",
    state: "State",
    zip: "Zip",
    country: "Country",
    phoneNumber: "Phone number",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_FROM_FIELD_IDS)[number],
    string
>;

const TO_FIELD_NAMES = {
    to: "To",
    // The settings row stays keyed "company" so saved field settings keep
    // applying. The printed value is the counterparty display name
    // (customer.name → party.businessName), not the invoice Company field.
    company: "Display name",
    fullName: "Contact name",
    companyName: "Company",
    email: "Email",
    street1: "Street line 1",
    street2: "Street line 2",
    city: "City",
    state: "State",
    zip: "Zip",
    country: "Country",
    phoneNumber: "Phone number",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_TO_FIELD_IDS)[number],
    string
>;

// Row names for the "Payment" section (PRO-12178): Invoice number, PO
// number, Terms, Invoice date, Due date. `termsType` labels the structured
// Terms dropdown selection (NativeInvoiceTermsField) - distinct from `terms`
// in NOTES_FIELD_NAMES below, which is the free-text "Payment instructions"
// field.
const DETAILS_FIELD_NAMES = {
    invoiceNumber: "Invoice number",
    poNumber: "PO number",
    termsType: "Terms",
    invoiceDate: "Invoice date",
    dueDate: "Due date",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS)[number],
    string
>;

// Row names for the "Notes" section (PRO-12178): Memo, Payment instructions -
// the free-text fields from CustomizeSection (printed headings default to
// NATIVE_INVOICE_LABEL_DEFAULTS.memo/paymentInstructions).
const NOTES_FIELD_NAMES = {
    memo: "Memo",
    terms: "Payment instructions",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_NOTES_FIELD_IDS)[number],
    string
>;

const ITEMS_TABLE_FIELD_NAMES = {
    item: "Item",
    quantity: "Quantity",
    rate: "Rate",
    tax: "Tax",
    discount: "Discount",
    deposit: "Deposit",
    amount: "Amount",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS)[number],
    string
>;

const TOTALS_FIELD_NAMES = {
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    deposit: "Deposit",
    totalDue: "Total due",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS)[number],
    string
>;

const INCLUSION_LOCKED_FIELD_IDS = new Set<string>(["businessName", "company"]);
const HIDDEN_BY_DEFAULT_FIELD_IDS = new Set<string>(["companyName"]);

function toFieldDefinitions<Id extends string>(
    ids: readonly Id[],
    names: Record<Id, string>,
): InvoiceFieldDefinition[] {
    return ids.map((id) => ({
        id,
        name: names[id],
        canHide: !INCLUSION_LOCKED_FIELD_IDS.has(id),
        isIncludedByDefault: !HIDDEN_BY_DEFAULT_FIELD_IDS.has(id),
    }));
}

export const HEADER_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_HEADER_FIELD_IDS,
    HEADER_FIELD_NAMES,
);

export const FROM_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_FROM_FIELD_IDS,
    FROM_FIELD_NAMES,
);

export const TO_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_TO_FIELD_IDS,
    TO_FIELD_NAMES,
);

export const DETAILS_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS,
    DETAILS_FIELD_NAMES,
);

export const NOTES_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_NOTES_FIELD_IDS,
    NOTES_FIELD_NAMES,
);

export const ITEMS_TABLE_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS,
    ITEMS_TABLE_FIELD_NAMES,
);

export const TOTALS_FIELD_DEFINITIONS = toFieldDefinitions(
    NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS,
    TOTALS_FIELD_NAMES,
);
